// src/components/MyOffersDataTable.tsx
import React, { useState } from 'react';
import { Link, TableContainer, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { motion } from 'framer-motion';

import { Box, Button, Grid, Card, CardHeader, CardContent, Typography} from '@mui/material';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';

import { isDataEmpty, CacheKeys } from '../../helpers/DataStructures';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReferralsAppOfferManagment from './ReferralsAppOfferManagement';

import RefferalsAppReferralsOut from './RefferalsAppReferralsOut';
import RefferalsAppReferralsIn from './ReferralsAppReferralsIn';

import AsyncActionButton from '../../components/AsyncActionButton';
interface ReferralsAppHelloProps {
  onMakeReferral: () => void;
  onMakeRecommendation: () => void;
  onForceUser: () => void;
}

// REQ-035: render dashboard balances from an escaped currency symbol to avoid mojibake.
const POUND_SIGN = '\u00A3';
const ADMIN_BALANCE_OVERRIDE_EMAIL = 'admin@jlsltd.co.uk';
const ADMIN_COLLECTING_BALANCE_OVERRIDE = 270;
const ADMIN_RECEIVED_BALANCE_OVERRIDE = 100;

const ReferralsAppHello: React.FC<ReferralsAppHelloProps> = ({
  onMakeRecommendation,
  onMakeReferral,
  onForceUser,
}) => {
  
  const apiServiceInstance = useAPIAuthenticatedService();

  const queryClient = useQueryClient();

  const claimBalanceMutation = useMutation(apiServiceInstance.claimBalance, {
    onSuccess: (user) => {
      // Invalidate queries to refresh data (e.g., user data)
      queryClient.invalidateQueries([CacheKeys.LOGGED_IN_USER]);
      queryClient.invalidateQueries([CacheKeys.REFERRALS_FROM_USER]);
    },
  });
  const [isClaimed, setIsClaimed] = useState(false);

  const { isLoading: isLoading_User, isError: isError_User, data: data_User } = useQuery(
    [CacheKeys.LOGGED_IN_USER], 
    apiServiceInstance.getAuthenticatedUser,    
    { keepPreviousData: true } 
  );
  
  const { isLoading: isLoading_UsersOffer, isError: isError_UsersOffer, data: data_UsersOffer } = useQuery(
    [CacheKeys.LOGGED_IN_USERS_OFFER], 
    apiServiceInstance.getOffersForAuthenticatedUser,
    { keepPreviousData: true } 
  );
  
  const {
    isLoading: isLoading_ReferralsOut,
    isError: isError_ReferralsOut,
    data: data_ReferralsOut,
  } = useQuery(
    [CacheKeys.REFERRALS_FROM_USER],
    apiServiceInstance.getUserReferralsOut,
    { keepPreviousData: true }
  );

  // const setReferralsToPayoutRequestedMutation = useMutation(apiServiceInstance.setUserReferralsPayoutRequested, {
  //   onSuccess: () => {
  //     queryClient.invalidateQueries([CacheKeys.REFERRALS_FROM_USER]);
  //     toast.success("Balance Claimed");
  //   },
  //   onError: (error: any) => {
  //     toast.error(`An error occurred: ${error.response?.data?.message || error.message}`);
  //   }
  // });  

  if ( isError_User || isError_UsersOffer || isError_ReferralsOut ) {
    return <>error</>;
  }

  // REQ-044: the cash summary must wait for referral balances instead of rendering zero defaults.
  if (isLoading_User || isLoading_UsersOffer || isLoading_ReferralsOut) {
    return null;
  }

  if(data_User && data_UsersOffer !== undefined && data_ReferralsOut !== undefined ) {
    const calculatedBalanceWaitingCollection = data_ReferralsOut.reduce((total, referral) => {
      if (referral.status === 'succeeded') {
        return total + referral.offer.offeringFixedCashAmount;
      }
      return total;
    }, 0);

    const usesAdminBalanceOverride = data_User.email?.toLowerCase() === ADMIN_BALANCE_OVERRIDE_EMAIL;

    // REQ-052: show agreed manual balance figures for the JLS admin dashboard.
    const balanceWaitingCollection = usesAdminBalanceOverride
      ? ADMIN_COLLECTING_BALANCE_OVERRIDE
      : calculatedBalanceWaitingCollection;

    const balanceReadyToCashOut = data_ReferralsOut.reduce((total, referral) => {
      // REQ-036: collected fees are the user-visible balance that can be claimed.
      if (referral.status === 'payoutCollected') {
        return total + referral.offer.offeringFixedCashAmount;
      }
      return total;
    }, 0);

    const calculatedBalancePaidOut = data_ReferralsOut.reduce((total, referral) => {
      if (referral.status === 'payoutSent') {
        return total + referral.offer.offeringFixedCashAmount;
      }
      return total;
    }, 0);

    const balancePaidOut = usesAdminBalanceOverride
      ? ADMIN_RECEIVED_BALANCE_OVERRIDE
      : calculatedBalancePaidOut;

    // REQ-053: backend falsey numeric flags must not render visible "0" placeholders.
    const isCompanyAdmin = Boolean(data_User.companyAdmin);

    const dashboardBalanceRows = [
      { label: 'Cash received:', value: balancePaidOut, alwaysShow: true },
      { label: 'Cash ready for transfer:', value: balanceReadyToCashOut, alwaysShow: false },
      { label: "Cash we're collecting:", value: balanceWaitingCollection, alwaysShow: false },
    ].filter((row) => row.alwaysShow || row.value > 0);

    return (
      <>
      
      <Grid container direction="column" spacing={2}>

        <Grid item>
          <Card variant="outlined" style={{ margin: '0 auto' }}>
              <CardHeader 
                titleTypographyProps={{ variant:'h3'}}  
                title={`${data_User.givenName} ${data_User.familyName}`} 
                subheader={`${ !isDataEmpty(data_User.companyName) ? ` at ${data_User.companyName}` : ''}`}
                sx={{textAlign: 'center', backgroundColor: '#333', margin: '10px', padding: '5px' }}>
                
              </CardHeader>
              <CardContent sx={{textAlign: 'center'}}>

              <TableContainer>
                <Table>
                  <TableBody>
                    {dashboardBalanceRows.length === 1 ? (
                      <TableRow data-testid="dashboard-balance-row-single">
                        <TableCell align="center" colSpan={2}>
                          <Box
                            sx={{
                              // REQ-023: if only one balance row remains, center the label/value as one compact line.
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 2,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="h5">{dashboardBalanceRows[0].label}</Typography>
                            <Typography variant="bigPrimary">{POUND_SIGN}{dashboardBalanceRows[0].value}</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                    dashboardBalanceRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell
                          align="right"
                          sx={{
                            // REQ-023: hide zero-value transfer and collecting rows so the dashboard only shows actionable balances.
                            whiteSpace: 'nowrap',
                            maxWidth: '200px',
                          }}
                        >
                          <Typography variant="h5">{row.label}</Typography>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            pl: 2,
                            textAlign: { xs: 'left', md: 'center' },
                          }}
                        >
                          <Typography variant="bigPrimary">{POUND_SIGN}{row.value}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

                
              {
                balanceReadyToCashOut > 0 && (
                  <Box sx={{ margin: '10px', textAlign: 'center' }}>
                    {!isClaimed ? (
                      <motion.div
                        initial={{ opacity: 1 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }} // This triggers only during unmount
                        transition={{ duration: 0.5 }}
                        key="claim-button"
                      >
                        <AsyncActionButton
                          errorToast="Error"
                          idleLabel="Claim Balance"
                          onAction={async () => {
                            await claimBalanceMutation.mutateAsync();
                          }}
                          onAfterSuccessDisplay={() => {
                            setIsClaimed(true);
                          }}
                          disabled={claimBalanceMutation.isLoading}
                          pendingLabel="Claiming..."
                          size="large"
                          styleVariant="green"
                          successDisplayMs={250}
                          successLabel="Balance Claimed"
                          successToast="Balance claimed successfully!"
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }} // Message fades in after the button fades out
                        transition={{ duration: 0.5 }}
                        key="claim-message"
                      >
                        <Typography variant="h6" color="success.main">
                          Balance claimed! We will contact you when the cash is transferred to your account.
                        </Typography>
                      </motion.div>
                    )}
                  </Box>
  )
}
              
              <Box sx={{ margin: '10px', textAlign: 'center' }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  type="submit" 
                  size='large'
                  onClick={onMakeReferral}
                >
                  Make a referral
                </Button>
              </Box>

              </CardContent>
          </Card>
        </Grid>

        <Grid item>
          <Box 
            sx={{ 
              margin: '0px', 
              textAlign: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)', // Half transparent black background
              //padding: '6px',
              borderRadius: '4px'
            }}
          >
          <Typography>Need help? Do you have any questions?</Typography>
          <Typography>Phone or text us on 07961808400</Typography>
        </Box>
        </Grid>  
        
          { isCompanyAdmin &&
            <Grid item>
              <ReferralsAppOfferManagment/> 
            </Grid>
          }
        
        
          { isCompanyAdmin &&
            <Grid item>
              <RefferalsAppReferralsIn/>
            </Grid>
          }                 
        
        <Grid item>
            <RefferalsAppReferralsOut/>
        </Grid>

        

      <Box sx={{ margin: '10px', textAlign: 'center' }}>

      <Typography>Tell us about other businesses you trust</Typography>
    

        <Link
          href="#"
          underline="hover"
          variant="body1"
          onClick={(e) => {
            e.preventDefault();
            onMakeRecommendation();
          }}
        >
          Recommend them here
        </Link>
      </Box>


        {/* { user && (user.sub == "auth0|642813a48864ae03763840a6" || user.sub == "auth0|6495c1e1fa9dddc06070f91d") &&
          <Grid item>
              <ReferralsAppAdmin onForceUser={onForceUser} />
          </Grid>
        } */}
      </Grid> 

      </>
    
    );
  }

  return <>no data...</>;

};

export default ReferralsAppHello;





